import {
  MarketplaceApi,
  MarketplaceSearchRequestInput,
  RequestOptions,
} from '@agilelab/plugin-wb-marketplace-common';
import {
  CollatorType,
  WitboostMarketplaceSearchResultSet,
} from '@agilelab/plugin-wb-search-common';
import { parseUrn } from '@agilelab/plugin-wb-platform-common';

export function filterValidMarketplaceUrns(ids: string[]): string[] {
  return ids.filter(id => {
    try {
      parseUrn(id);
      return true;
    } catch {
      return false;
    }
  });
}

type SelectedConsumerResultSet = Omit<
  WitboostMarketplaceSearchResultSet,
  'results'
> & {
  numberOfResults: number;
  results: Array<
    WitboostMarketplaceSearchResultSet['results'][number] & {
      external_id: string;
    }
  >;
};

export async function getSelectedSearchResults(
  api: Pick<MarketplaceApi, 'search'>,
  request: MarketplaceSearchRequestInput & { category: 'selected' },
  options?: RequestOptions & { allPages?: boolean },
): Promise<SelectedConsumerResultSet> {
  const results: SelectedConsumerResultSet['results'] = [];
  const cursors = new Set<string>();
  let cursor = request.cursor;
  let numberOfResults = 0;
  do {
    if (options?.signal?.aborted)
      throw new DOMException('Aborted', 'AbortError');
    const page = await api.search(
      {
        ...request,
        select: [
          ...new Set(['external_id', ...(request.select ?? ['document'])]),
        ],
        cursor,
      },
      options,
    );
    numberOfResults = page.totals.items;
    for (const item of page.items) {
      if (!item.external_id || !item.document) {
        throw new Error(
          'Marketplace search returned an incomplete selected result',
        );
      }
      results.push({
        ...item,
        type: CollatorType.MARKETPLACE_PROJECTS,
        document: item.document,
      } as SelectedConsumerResultSet['results'][number]);
    }
    cursor = page.nextCursor;
    if (cursor && cursors.has(cursor)) {
      throw new Error('Marketplace search returned a repeated cursor');
    }
    if (cursor) cursors.add(cursor);
  } while (cursor && options?.allPages);

  return { results, numberOfResults, nextPageCursor: cursor };
}
